import * as React from "react";

import { useEditorDispatch, useEditorStore } from "@v1/store/store";
import { flushSync } from "react-dom";
import { getAlchemyEditorWindow } from "replo-runtime/shared/Window";

import { handleTransformWillChange } from "./canvas-actions";
import {
  selectCanvasArea,
  selectCanvasInteractionMode,
  selectCanvasScale,
  zoomCanvas,
} from "./canvas-reducer";
import { clientPosToTranslatedPos, scaleFromPoint } from "./canvas-utils";
import { useSetDeltaXY } from "./useSetDeltaXY";

export function useCanvasZoom() {
  const setDeltaXY = useSetDeltaXY();
  const dispatch = useEditorDispatch();
  const store = useEditorStore();

  const handleCanvasZoom = React.useCallback(
    (newScale: number) => {
      const canvasArea = selectCanvasArea(store.getState());
      const canvasScale = selectCanvasScale(store.getState());
      const canvasInteractionMode = selectCanvasInteractionMode(
        store.getState(),
      );
      // Note (Noah, 2022-08-04): We need to wrap this whole thing in
      // flushSync because React 18's state batching can cause the scale
      // calculation to work incorrectly (because each successive scale
      // operation depends on the last scale value, batching them causes
      // issues)
      flushSync(() => {
        if (canvasInteractionMode === "locked") {
          return;
        }

        dispatch(handleTransformWillChange());

        dispatch(zoomCanvas(newScale));
        setDeltaXY((prevState) => {
          if (!canvasArea) {
            return prevState;
          }

          const referentialPos = clientPosToTranslatedPos(
            canvasArea,
            {
              x: canvasArea.offsetWidth / 2,
              y: canvasArea.offsetHeight / 2,
            },
            { x: prevState.deltaX, y: prevState.deltaY },
          );
          const editorWindow = getAlchemyEditorWindow();
          if (editorWindow) {
            editorWindow.alchemyEditor.canvasScale = newScale;
            editorWindow.alchemyEditor.canvasYOffset = prevState.deltaY;
          }
          return scaleFromPoint(
            canvasScale,
            newScale,
            prevState.deltaX,
            prevState.deltaY,
            referentialPos,
          );
        });
      });
    },
    [dispatch, setDeltaXY, store],
  );

  const handleCanvasZoomIn = React.useCallback(() => {
    const canvasScale = selectCanvasScale(store.getState());
    handleCanvasZoom(canvasScale * 1.1);
  }, [handleCanvasZoom, store]);

  const handleCanvasZoomOut = React.useCallback(() => {
    const canvasScale = selectCanvasScale(store.getState());
    handleCanvasZoom(canvasScale / 1.1);
  }, [handleCanvasZoom, store]);

  return React.useMemo(
    () => ({ handleCanvasZoom, handleCanvasZoomIn, handleCanvasZoomOut }),
    [handleCanvasZoom, handleCanvasZoomIn, handleCanvasZoomOut],
  );
}
